import { useRef, useState } from "react";
import { toast } from "sonner";
import { FileDown, FileUp } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Block } from "./Common";

const KINDS = [
  ["members", "Anggota", "Kolom: Nama; Fungsi; Email; PIN; No WhatsApp; Status lapor; Aktif. Nama yang sudah ada dilewati."],
  ["reports", "Laporan", "Kolom sama dengan Ekspor CSV (Tanggal; Kode; Nama; Fungsi; Program; Status; Aktivitas; …; kolom KPI). Laporan dengan tanggal + anggota yang sama dilewati."],
];

async function downloadTemplate(kind) {
  const res = await api.get(`/import/template/${kind}`, { responseType: "blob" });
  const url = URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `template-${kind === "members" ? "anggota" : "laporan"}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function Result({ r }) {
  return (
    <div data-testid="import-result" className="mt-3 rounded-xl bg-brand-soft p-3 text-sm">
      <p className="font-bold">{r.created} ditambahkan · {r.skipped} dilewati (sudah ada) · {r.errorCount} gagal</p>
      {r.errors.length > 0 && (
        <ul className="mt-2 max-h-40 list-disc overflow-y-auto pl-5 text-xs">
          {r.errors.map((e) => <li key={e.row}>Baris {e.row}: {e.message}</li>)}
        </ul>
      )}
      {r.generatedPins?.length > 0 && (
        <div className="mt-2">
          <p className="text-xs font-bold text-brand">PIN dibuat otomatis — catat & bagikan sekarang, tidak ditampilkan lagi:</p>
          <ul className="mt-1 text-xs" data-testid="import-generated-pins">
            {r.generatedPins.map((p) => <li key={p.name}>{p.name}: <b className="tabular-nums">{p.pin}</b></li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function PanelImport({ onDone }) {
  const refs = { members: useRef(null), reports: useRef(null) };
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState({});
  const [url, setUrl] = useState("");
  const [sheetRes, setSheetRes] = useState(null);
  const importSheet = async () => {
    setBusy("sheet");
    try {
      const r = await api.post("/import/sheet", { url });
      setSheetRes(r.data);
      toast.success(`Impor sheet selesai: ${r.data.members.created} anggota, ${r.data.reports.created} laporan`);
      onDone();
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy("");
    }
  };
  const upload = async (kind, e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(kind);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/import/${kind}`, fd);
      setResult((p) => ({ ...p, [kind]: r.data }));
      toast.success(`Impor selesai: ${r.data.created} ditambahkan`);
      onDone();
    } catch (ex) {
      toast.error(errMsg(ex));
    } finally {
      setBusy("");
    }
  };
  return (
    <Block title="Impor CSV dari Google Sheet" testid="panel-import">
      <p className="mb-4 text-xs text-[#7a6667]">Impor anggota dulu, lalu laporan. Pemisah ; atau , terdeteksi otomatis.</p>
      <div className="mb-5 rounded-xl border border-brand-line p-3">
        <span className="label">Langsung dari Google Sheet versi lama</span>
        <p className="mb-2 text-xs text-[#7a6667]">Tab Team, Reports, ReportHistory, Blockers, Leaves, Settings, ReminderLog. Data yang sudah ada dilewati. PIN lama tetap berlaku.</p>
        <div className="flex gap-2">
          <input data-testid="import-sheet-url" className="field flex-1" placeholder="https://docs.google.com/spreadsheets/d/…" value={url} onChange={(e) => setUrl(e.target.value)} />
          <button data-testid="import-sheet-button" disabled={!!busy || !url} onClick={importSheet} className="btn-primary shrink-0">{busy === "sheet" ? "Mengimpor…" : "Impor"}</button>
        </div>
        {sheetRes && (
          <p data-testid="import-sheet-result" className="mt-2 rounded-lg bg-brand-soft p-2 text-xs">
            Anggota {sheetRes.members.created} baru ({sheetRes.members.skipped} dilewati) · Laporan {sheetRes.reports.created} baru ({sheetRes.reports.skipped} dilewati, {sheetRes.reports.errors} gagal) · Riwayat {sheetRes.history} · Blocker {sheetRes.blockers} · Cuti {sheetRes.leaves}
          </p>
        )}
      </div>
      <div className="space-y-5">
        {KINDS.map(([kind, label, hint]) => (
          <div key={kind}>
            <span className="label">{label}</span>
            <p className="mb-2 text-xs text-[#7a6667]">{hint}</p>
            <input ref={refs[kind]} data-testid={`import-${kind}-input`} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => upload(kind, e)} />
            <div className="flex flex-wrap gap-2">
              <button data-testid={`import-${kind}-button`} disabled={!!busy} onClick={() => refs[kind].current?.click()} className="btn-primary">
                <FileUp size={15} /> {busy === kind ? "Mengimpor…" : `Impor ${label.toLowerCase()}`}
              </button>
              <button data-testid={`import-${kind}-template`} onClick={() => downloadTemplate(kind).catch((e) => toast.error(errMsg(e)))} className="btn-ghost">
                <FileDown size={15} /> Template
              </button>
            </div>
            {result[kind] && <Result r={result[kind]} />}
          </div>
        ))}
      </div>
    </Block>
  );
}
